import {
  Building2,
  Briefcase,
  CalendarCheck,
  CheckSquare,
  ClipboardCheck,
  FolderOpen,
  LayoutDashboard,
  ListChecks,
  Network,
  Settings,
  BarChart3,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { ALL_MODULES_ON, type ModuleKey, type ModuleState, type PermissionObject, type SessionPayload } from '@nest-recruit/shared';

// Level 2 of the shell — the module bar.
//
// "The module bar is permission-driven. Items render based on the current user's
// permissions, not a static list. A panelist sees one item." (00-MASTER-PLAN S2.)
// That last sentence is the acceptance test, and it is why this is a config plus a
// rule rather than eight hard-coded roles: a panelist who signs in to eight modules
// they cannot use is a panelist who stops signing in.
//
// **It reads the server's resolved permissions, not role names.** S2 asked the
// §L23 matrix what a role could do, which was right until S4 let an HR Lead clone
// "Recruiter" into "Recruiter, read-only" (F2.08). A browser reasoning from the
// role's NAME would still show that person every Recruiter menu item. The matrix in
// packages/shared is now the source of the defaults; the session payload is the
// source of truth for a given viewer.

export const MODULE_IDS = [
  'dashboard',
  'organisation',
  'requisitions',
  'jobs',
  'candidates',
  'assessments',
  'interviews',
  'tasks',
  'reports',
  'admin',
] as const;

/** S10: the hiring manager's whole world (E16) — three items and nothing else. */
export const HM_MODULE_IDS = ['my_actions', 'my_openings', 'request_hire'] as const;

export type ModuleId = (typeof MODULE_IDS)[number] | (typeof HM_MODULE_IDS)[number];

export interface NavItem {
  readonly id: ModuleId;
  /** Sentence case, always (06-qol §11). */
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to: string;
  /**
   * The §L23 object this module *is*. Read access to it is what makes the module a
   * place the user can go.
   */
  readonly object: PermissionObject;
  /** The letter in the `g`-then-letter shortcut (06-qol §2). Not every module has one. */
  readonly goKey?: string;
  /** R1 (§L34): the switchable module this item belongs to; hidden while it is off. */
  readonly module?: ModuleKey;
  /**
   * Screens inside the module. When present, clicking the module opens a menu of
   * them rather than navigating, so none of them is reachable only by typing.
   */
  readonly pages?: readonly NavPage[];
}

export interface NavPage {
  readonly label: string;
  readonly to: string;
  /**
   * The §L23 object this screen is, when it differs from its module's. S18: an
   * invigilator holds exam sessions and nothing else under Assessments, so they see
   * that one page — a module is open when any of its pages is.
   */
  readonly object?: PermissionObject;
  /** R1 (§L34): the switchable module this page belongs to, when it is not its item's. */
  readonly module?: ModuleKey;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    to: '/',
    object: 'reports_own',
    goKey: 'd',
    module: 'workspace',
  },
  {
    /**
     * S5. Its own module rather than a tab under Admin, because §L23 gives
     * Recruiters read access to positions and no admin access at all — putting the
     * org tree under Admin would hide it from exactly the people who use it most.
     */
    id: 'organisation',
    label: 'Organisation',
    icon: Network,
    to: '/organisation',
    object: 'org_unit_position',
    goKey: 'o',
    module: 'organisation',
    pages: [
      { label: 'Org tree', to: '/organisation' },
      { label: 'Positions', to: '/organisation/positions' },
      { label: 'Headcount plan', to: '/organisation/headcount' },
    ],
  },
  {
    id: 'requisitions',
    label: 'Requisitions',
    icon: Building2,
    to: '/requisitions',
    object: 'requisition',
    goKey: 'r',
    pages: [
      { label: 'Requisition board', to: '/requisitions', module: 'requisitions' },
      // Workflow review 2026-10-29: hire-request approvals are on the board ("Waiting on me").
      // The page stays only for offer approvals, so it shows only while Offers is on (§L34).
      { label: 'Approvals', to: '/approvals', module: 'offers' },
      { label: 'Request a hire', to: '/requisitions/new', module: 'requisitions' },
    ],
  },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, to: '/jobs', object: 'job_opening', goKey: 'j' },
  {
    id: 'candidates',
    label: 'Candidates',
    icon: Users,
    to: '/candidates',
    object: 'candidate',
    goKey: 'c',
    // S25: the pool screens sit beside the list (POOL-04 is folded into it, Redesign Group 1). Pools are §L23's own object.
    pages: [
      { label: 'All candidates', to: '/candidates' },
      { label: 'Talent pools', to: '/pools', object: 'talent_pool', module: 'pools' },
      // ADR 0046: §L23 Candidate, so a panelist's interviewee-only reach never shows it.
      { label: 'CV Bank', to: '/cv-bank', module: 'cv_bank' },
    ],
  },
  {
    /**
     * S17. Its own module, not a page under Admin: §L23 gives recruiters the bank and
     * the builder, and no admin access at all (the same reasoning as Organisation).
     */
    id: 'assessments',
    label: 'Assessments',
    icon: ClipboardCheck,
    to: '/assessments',
    object: 'assessment_template',
    module: 'assessments',
    pages: [
      { label: 'Tests', to: '/assessments' },
      { label: 'Question bank', to: '/assessments/questions' },
      { label: 'Exam sessions', to: '/assessments/sessions', object: 'exam_session', module: 'exam_sessions' },
      { label: 'Marking', to: '/assessments/marking', object: 'assessment_attempt' },
    ],
  },
  {
    /**
     * S19. "To schedule" is recruiter work: it sits behind the §L23 "Job config /
     * interview plan" object (HRL F, REC S), which a panelist or hiring manager does not
     * hold — they get the calendar of their own interviews, "My interviews" and their
     * availability.
     */
    id: 'interviews',
    label: 'Interviews',
    icon: CalendarCheck,
    to: '/interviews',
    object: 'interview',
    goKey: 'i',
    pages: [
      { label: 'Calendar', to: '/interviews' },
      { label: 'To schedule', to: '/interviews/to-schedule', object: 'job_config' },
      { label: 'My interviews', to: '/interviews/mine' },
      { label: 'My availability', to: '/interviews/availability' },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: CheckSquare,
    to: '/tasks',
    object: 'activity_message',
    goKey: 't',
    module: 'tasks',
  },
  {
    // S28: the command center is the HR Lead's ("Reports — all staff"); the definitions are everyone's.
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    to: '/reports',
    object: 'reports_own',
    module: 'reports',
    // S29: proficiency is the HR Lead's and each recruiter's own; the scoreboard is the HR Lead's here
    // (a hiring manager reaches their own department's from My openings — S10 keeps their bar at three).
    pages: [
      { label: 'Command center', to: '/reports/command-center', object: 'reports_all_staff' },
      { label: 'Proficiency', to: '/reports/proficiency' },
      { label: 'Department scoreboard', to: '/reports/scoreboard', object: 'reports_all_staff' },
      { label: 'Drop-off', to: '/reports/drop-off' },
      { label: 'Report builder', to: '/reports/builder' },
      { label: 'Metric definitions', to: '/reports/metrics' },
    ],
  },
  // ADR 0073: HR reaches the hiring set-up pages here too; every Admin holds hiring_setup.
  { id: 'admin', label: 'Admin', icon: Settings, to: '/admin', object: 'hiring_setup' },
];

/**
 * HM-02, HM-01, HM-03 — in the order a manager uses them: what is waiting on me,
 * how my openings are doing, and asking for someone new. My actions is home.
 */
export const HM_NAV_ITEMS: readonly NavItem[] = [
  { id: 'my_actions', label: 'My actions', icon: ListChecks, to: '/my/actions', object: 'requisition', goKey: 'a' },
  // Both are built on requisitions (HM-01's cards are requests), so they go with Hire requests.
  { id: 'my_openings', label: 'My openings', icon: FolderOpen, to: '/my/openings', object: 'requisition', goKey: 'o', module: 'requisitions' },
  { id: 'request_hire', label: 'Request a hire', icon: UserPlus, to: '/request-hire', object: 'requisition', goKey: 'n', module: 'requisitions' },
];

/**
 * Scopes that mean "reachable only through a parent record", never a browsable list.
 *
 * This is the one judgement call in the mechanism, so it is stated rather than
 * buried. §L23 grants a panelist `Candidate | S (interviewees only)` — they read the
 * candidate record from inside the interview they are sitting on. That is not the
 * same as the candidate directory being somewhere they can navigate to, and treating
 * it as the same is what would put a second, useless item in a panelist's module bar.
 *
 * `own_only` is deliberately NOT in this set: "my interviews" and "my scorecards" are
 * genuine lists, just short ones.
 */
const RECORD_BOUND_SCOPES: ReadonlySet<string> = new Set([
  'interviewees_only',
  // S18: an invigilator reaches test attempts only through their own exam session —
  // the seats on its list — so Marking is not a place for them.
  'own_session',
  // ADR 0085: an HR assistant sees a candidate's phone and email on the contact list and the
  // interview calendar, never the candidate directory.
  'contact_details_only',
]);

/** What the nav rules need from a session — the permission map and the surface. */
export type NavPermissions = Pick<SessionPayload, 'permissions' | 'isHiringManagerSurface'>;

const HM_IDS: ReadonlySet<ModuleId> = new Set(HM_MODULE_IDS);

/**
 * Whether a module is a destination for this viewer. A hiring manager gets the three
 * HM screens and nothing else (master plan S10: "HM module bar shows only these three
 * items"); everyone else gets the full application and never the HM screens.
 */
export function canOpenModule(item: NavItem, session: NavPermissions | null, modules: ModuleState = ALL_MODULES_ON): boolean {
  if (!session) return false;
  if (HM_IDS.has(item.id) !== session.isHiringManagerSurface) return false;
  if (item.module && !modules[item.module]) return false;
  if (item.pages) return pagesFor(item, session, modules).length > 0;
  return canBrowse(item.object, session);
}

function canBrowse(object: PermissionObject, session: NavPermissions): boolean {
  const cell = session.permissions[object];
  if (!cell || cell.level === 'none') return false;
  return cell.scope === undefined || !RECORD_BOUND_SCOPES.has(cell.scope);
}

/** A module's screens this viewer can open; a page without its own object is its module's. */
export function pagesFor(item: NavItem, session: NavPermissions | null, modules: ModuleState = ALL_MODULES_ON): readonly NavPage[] {
  if (!session || !item.pages) return [];
  return item.pages.filter((page) => canBrowse(page.object ?? item.object, session) && (!page.module || modules[page.module]));
}

/** Where the module takes this viewer: its first screen they can open. */
export function destinationFor(item: NavItem, session: NavPermissions | null, modules: ModuleState = ALL_MODULES_ON): string {
  return pagesFor(item, session, modules)[0]?.to ?? item.to;
}

/**
 * The module bar for a viewer, in the fixed order above, each with only the pages they can open
 * and only what Master settings has on (§L28: a menu left empty disappears).
 */
export function navItemsFor(session: NavPermissions | null, modules: ModuleState = ALL_MODULES_ON): readonly NavItem[] {
  const items = session?.isHiringManagerSurface ? HM_NAV_ITEMS : NAV_ITEMS;
  return items.filter((item) => canOpenModule(item, session, modules)).map((item) => (item.pages ? { ...item, pages: pagesFor(item, session, modules) } : item));
}

/** The `g`-then-letter map for the modules this viewer can actually reach (06-qol §2). */
export function goShortcutsFor(session: NavPermissions | null, modules: ModuleState = ALL_MODULES_ON): ReadonlyMap<string, NavItem> {
  const map = new Map<string, NavItem>();
  for (const item of navItemsFor(session, modules)) {
    if (item.goKey) map.set(item.goKey, item);
  }
  return map;
}
